<template>
  <div>
    <b-modal
      :id="modalId"
      :visible="showModal"
      @hide="cancel"
      title="Associate Parent Account"
      header-class="modal-header"
      body-class="modal-body"
      centered
      hide-footer
    >
      <div class="associate-parent-modal-content">
        <div v-if="isScannerActive" class="scanner-container">
          <video ref="scannerVideo" class="scanner" autoplay playsinline></video>
          <p v-if="scannerError" class="scanner-error">{{ scannerError }}</p>
        </div>
        <button v-else class="btn scan-btn" @click="startScanner" :disabled="loading">Scan QR Code</button>
        <div class="divider">
          <span>OR</span>
        </div>
        <div class="manual-input-container">
          <label for="parent-code" class="form-label">Enter Parent Code</label>
          <input
            id="parent-code"
            type="text"
            v-model="parentCode"
            class="form-control"
            placeholder="Enter parent account code"
            :disabled="loading"
          />
        </div>
        <div class="modal-actions">
          <button class="btn border-btn" @click="cancel" :disabled="loading">Cancel</button>
          <button class="btn primary-btn" @click="handleAssociate" :disabled="loading || !parentCode">
            <b-spinner small v-if="loading"></b-spinner>
            <span v-else>Associate</span>
          </button>
        </div>
      </div>
    </b-modal>

    <!-- Confirmation Modal -->
    <b-modal
      id="confirm-associate-modal"
      v-model="showConfirmModal"
      title="Confirm Association"
      header-class="modal-header"
      body-class="modal-body"
      centered
      hide-footer
    >
      <div class="confirm-modal-content">
        <p>Are you sure you want to associate with parent code <strong>{{ parentCode }}</strong>? Please verify the code is correct.</p>
        <div class="modal-actions">
          <button class="btn border-btn" @click="showConfirmModal = false" :disabled="loading">Cancel</button>
          <button class="btn primary-btn" @click="associate" :disabled="loading">
            <b-spinner small v-if="loading"></b-spinner>
            <span v-else>Confirm</span>
          </button>
        </div>
      </div>
    </b-modal>
  </div>
</template>

<script>
import QrScanner from 'qr-scanner';
import { mapActions, mapState } from 'vuex';

export default {
  name: 'AssociateParentModal',
  props: {
    showModal: {
      type: Boolean,
      required: true,
    },
  },
  data() {
    return {
      modalId: 'associate-parent-modal',
      parentCode: '',
      scannerError: '',
      qrScanner: null,
      loading: false,
      isScannerActive: false,
      isManualInput: false, // Track if input is manual
      showConfirmModal: false,
    };
  },
  methods: {
    ...mapActions('user', ['assignChildrenToParent']),
    handleAssociate() {
      if (this.isManualInput) {
        this.showConfirmModal = true; // Show confirmation modal for manual input
      } else {
        this.associate(); // Proceed directly for QR code scans
      }
    },
    async associate() {
      if (!this.parentCode) {
        this.$toasted.error('Please enter or scan a parent code.', {
          duration: 5000,
          position: 'top-right',
          theme: 'toasted-primary',
        });
        this.showConfirmModal = false;
        return;
      }
      if (!this.user || !this.user.id) {
        this.$toasted.error('User ID is missing.', {
          duration: 5000,
          position: 'top-right',
          theme: 'toasted-primary',
        });
        this.loading = false;
        this.showConfirmModal = false;
        return;
      }
      this.loading = true;
      this.stopScanner(); // Stop scanner to prevent multiple scans
      try {
        const response = await this.assignChildrenToParent({
          parentID: this.parentCode,
          studentIDs: [this.user.id],
        });
        this.$toasted.success('Your account has been associated with parent successfully', {
          duration: 5000,
          position: 'top-right',
          theme: 'toasted-primary',
        });
        this.cancel();
      } catch (error) {
        this.$toasted.error("Failed to associate parent, user doesn't exist", {
          duration: 5000,
          position: 'top-right',
          theme: 'toasted-primary',
        });
      } finally {
        this.loading = false;
        this.showConfirmModal = false;
      }
    },
    cancel() {
      this.stopScanner();
      this.resetForm();
      this.$emit('updateModalState', false);
    },
    resetForm() {
      this.parentCode = '';
      this.parentCodeError = '';
      this.scannerError = '';
      this.loading = false;
      this.isScannerActive = false;
      this.isManualInput = false;
      this.showConfirmModal = false;
    },
    async startScanner() {
      this.isScannerActive = true;
      this.isManualInput = false; // Reset manual input flag
      await this.$nextTick(); // Wait for DOM to update
      await this.initScanner();
    },
    stopScanner() {
      if (this.qrScanner) {
        try {
          this.qrScanner.stop();
          this.qrScanner.destroy();
        } catch (error) {
          console.error('Error stopping scanner:', error);
        }
        this.qrScanner = null;
      }
    },
    async initScanner() {
      this.scannerError = '';

      const videoElement = this.$refs.scannerVideo;
      if (!videoElement) {
        this.scannerError = 'Scanner video element not found.';
        console.error('Video element not found');
        this.$toasted.error(this.scannerError, {
          duration: 5000,
          position: 'top-right',
          theme: 'toasted-primary',
        });
        this.isScannerActive = false;
        return;
      }

      if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
        this.scannerError = 'Camera not supported by this browser.';
        console.error('Camera not supported by browser');
        this.$toasted.error(this.scannerError, {
          duration: 5000,
          position: 'top-right',
          theme: 'toasted-primary',
        });
        this.isScannerActive = false;
        return;
      }

      if (!window.isSecureContext) {
        this.scannerError = 'Camera access requires a secure context (HTTPS).';
        console.error('Secure context required for camera access');
        this.$toasted.error(this.scannerError, {
          duration: 5000,
          position: 'top-right',
          theme: 'toasted-primary',
        });
        this.isScannerActive = false;
        return;
      }

      try {
        if (!QrScanner) {
          this.scannerError = 'QR Scanner not loaded.';
          console.error('QR Scanner library not loaded');
          this.$toasted.error(this.scannerError, {
            duration: 5000,
            position: 'top-right',
            theme: 'toasted-primary',
          });
          this.isScannerActive = false;
          return;
        }

        this.qrScanner = new QrScanner(videoElement, (result) => {
          this.parentCode = typeof result === 'string' ? result : result.data || '';
          this.isManualInput = false; // QR code scan, not manual
          this.associate();
        }, {
          returnDetailedScanResult: true,
          highlightScanRegion: true,
          highlightCodeOutline: true,
          maxScansPerSecond: 1,
        });

        const hasCamera = await QrScanner.hasCamera();
        if (!hasCamera) {
          this.scannerError = 'No camera found on this device.';
          console.error('No camera available');
          this.$toasted.error(this.scannerError, {
            duration: 5000,
            position: 'top-right',
            theme: 'toasted-primary',
          });
          this.isScannerActive = false;
          return;
        }

        await this.qrScanner.start();
      } catch (error) {
        console.error('Scanner initialization failed:', error);
        if (error.name === 'NotAllowedError') {
          this.scannerError = 'Camera access denied. Please allow camera permissions.';
        } else if (error.name === 'NotFoundError') {
          this.scannerError = 'No camera found on this device.';
        } else {
          this.scannerError = "Error initializing QR code scanner, Parent doesn't exist";
        }
        this.$toasted.error(this.scannerError, {
          duration: 5000,
          position: 'top-right',
          theme: 'toasted-primary',
        });
        this.isScannerActive = false;
      }
    },
  },
  computed: {
    ...mapState('user', ['user']),
  },
  beforeDestroy() {
    this.stopScanner();
  },
  watch: {
    parentCode(newValue) {
      if (newValue) {
        this.isManualInput = true; // Set flag when user types in the input
      }
    },
  },
};
</script>

<style scoped>
.associate-parent-modal-content {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 1.5rem;
  padding: 1rem;
  justify-content: center;
  width: 100%;
}
.confirm-modal-content{
  padding-bottom: 0.5rem;
}

.scanner-container {
  position: relative;
  width: 100%;
  height: 200px;
  background-color: #f8f9fa;
  border: 1px solid #d5d5d5;
  border-radius: 6px;
  overflow: hidden;
}

.scanner {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.scanner-error {
  color: #dc3545;
  font-size: 0.875rem;
  margin-top: 0.5rem;
  text-align: center;
}

.scan-btn {
  padding: 0.625rem 0.875rem;
  border: 1px solid #d5d5d5;
  border-radius: 6px;
  font-size: 0.875rem;
  color: #000;
  background-color: #fff;
}

.scan-btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.divider {
  position: relative;
  width: 100%;
  text-align: center;
  color: #6c757d;
  font-weight: 500;
}

.divider::before,
.divider::after {
  content: '';
  position: absolute;
  top: 50%;
  width: 40%;
  height: 1px;
  background-color: #d5d5d5;
}

.divider::before {
  left: 0;
}

.divider::after {
  right: 0;
}

.divider span {
  background-color: #fff;
  padding: 0 1rem;
}

.manual-input-container {
  width: 100%;
}

.form-label {
  font-size: 0.9rem;
  color: #000;
  margin-bottom: 0.5rem;
}

.form-control {
  border-color: #d5d5d5;
  border-radius: 6px;
  padding: 0.625rem;
  font-size: 0.875rem;
}

.modal-actions {
  display: flex;
  gap: 1rem;
  justify-content: flex-end;
  width: 100%;
}

.border-btn {
  padding: 0.625rem 0.875rem;
  border: 1px solid #d5d5d5;
  border-radius: 6px;
  font-size: 0.875rem;
  color: #000;
  background-color: #fff;
}

.border-btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.primary-btn {
  padding: 0.625rem 0.875rem;
  border: none;
  border-radius: 6px;
  font-size: 0.875rem;
  color: #fff;
  background-color: var(--primary-color, #007bff);
}

.primary-btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.primary-btn:hover:not(:disabled) {
  background-color: #0056b3;
}

.modal-header {
  border-bottom: 1px solid #d5d5d5;
}

.modal-body {
  padding: 1.5rem;
}



</style>